import React, { useMemo, useState } from 'react'
import { FaCreditCard, FaShieldAlt } from 'react-icons/fa'
import Banco from '@resources/imgs/Pagar-1.png'
import IOrder from '@interfaces/IOrder'
import PagarmeCheckoutModal from '@components/Modals/PagarmeCheckoutModal'

const PAGARME_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  paid: { label: 'Aprovado', color: '#155724', bg: '#d4edda' },
  processing: { label: 'Processando', color: '#856404', bg: '#fff3cd' },
  pending: { label: 'Pendente', color: '#856404', bg: '#fff3cd' },
  failed: { label: 'Recusado', color: '#721c24', bg: '#f8d7da' },
  canceled: { label: 'Cancelado', color: '#721c24', bg: '#f8d7da' },
}

interface PagarProps {
  inProgressOrder: IOrder | null
  price: number
  onPaymentSuccess?: () => void
}

const Pagar: React.FC<PagarProps> = ({ inProgressOrder, price, onPaymentSuccess }) => {
  const [showModal, setShowModal] = useState(false)
  const [paid, setPaid] = useState(false)

  const isPagarme = inProgressOrder?.payment_method === 'pagarme'
  const pagarmeStatus = inProgressOrder?.pagarme_status ?? null
  const statusInfo = pagarmeStatus ? (PAGARME_STATUS_MAP[pagarmeStatus] ?? { label: pagarmeStatus, color: '#495057', bg: '#e9ecef' }) : null
  const alreadyPaid = paid || statusInfo?.label === 'Aprovado'

  const formattedPrice = useMemo(() => {
    return price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }, [price])

  const handlePay = () => {
    if (!inProgressOrder || alreadyPaid) return
    setShowModal(true)
  }

  const handlePaymentSuccess = () => {
    setPaid(true)
    onPaymentSuccess?.()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Logo + Description */}
      <div className="d-flex align-items-center mb-3">
        <figure className="mb-0 mr-3" style={{ flexShrink: 0 }}>
          <img src={Banco} alt="Pagar.me" style={{ height: '32px', width: 'auto' }} />
        </figure>
        <div>
          <p className="mb-0" style={{ fontSize: '0.9rem', color: '#495057' }}>
            <b style={{ color: '#212529' }}>Pague com segurança</b> via Pagar.me — cartão de crédito parcelado.
          </p>
        </div>
      </div>

      {/* Installment options */}
      <div className="row mb-3">
        <div className="col-6">
          <div className="p-3 text-center" onClick={handlePay} style={{
            borderRadius: '8px',
            backgroundColor: '#E8EEFF',
            border: '1px solid #d0d8f0',
            cursor: 'pointer',
          }}
          >
            <FaCreditCard size={20} className="mb-2" style={{ color: '#4d61aa' }} />
            <p className="mb-0 font-weight-bold" style={{ fontSize: '0.9rem', color: '#4d61aa' }}>3x sem juros</p>
            <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Parcela fixa</p>
          </div>
        </div>
        <div className="col-6">
          <div className="p-3 text-center" onClick={handlePay} style={{
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            cursor: 'pointer',
          }}
          >
            <FaCreditCard size={20} className="mb-2" style={{ color: '#6c757d' }} />
            <p className="mb-0 font-weight-600" style={{ fontSize: '0.9rem', color: '#495057' }}>Até 12x</p>
            <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Com acréscimo</p>
          </div>
        </div>
      </div>

      {/* Price */}
      {price > 0 && (
        <div className="p-2 mb-3 d-flex align-items-center justify-content-between" style={{
          borderRadius: '8px',
          backgroundColor: '#fff8fa',
          border: '1px solid #fde2e9',
        }}
        >
          <span style={{ fontSize: '0.85rem', color: '#495057' }}>Valor a pagar:</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#FA5A84' }}>{formattedPrice}</span>
        </div>
      )}

      {/* Spacer to push button to bottom */}
      <div style={{ flexGrow: 1 }} />

      {/* Status or Pay Button */}
      {alreadyPaid || (isPagarme && statusInfo) ? (
        <div className="p-3" style={{
          borderRadius: '8px',
          backgroundColor: statusInfo?.bg ?? '#d4edda',
          border: `1.5px solid ${statusInfo?.color ?? '#155724'}33`,
        }}
        >
          <div className="d-flex align-items-center">
            <span style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: statusInfo?.color ?? '#155724',
              flexShrink: 0,
              display: 'inline-block',
              marginRight: '10px',
            }}
            />
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: statusInfo?.color ?? '#155724' }}>
              Status Pagar.me: {statusInfo?.label ?? 'Aprovado'}
            </span>
          </div>
        </div>
      ) : (
        <button
          className="btn btn-lg w-100 py-3"
          onClick={handlePay}
          disabled={!inProgressOrder}
          style={{
            background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontWeight: 'bold',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 12px rgba(250, 90, 132, 0.3)',
          }}
        >
          <div className="d-flex align-items-center justify-content-center">
            <FaCreditCard size={18} className="mr-2" />
            Realizar Pagamento
          </div>
        </button>
      )}

      {/* Security note */}
      <div className="d-flex align-items-center justify-content-center mt-3">
        <FaShieldAlt size={14} className="mr-2" style={{ color: '#adb5bd' }} />
        <span style={{ fontSize: '0.75rem', color: '#adb5bd' }}>Pagamento seguro via Pagar.me</span>
      </div>

      {/* Transparent Checkout Modal */}
      {inProgressOrder && (
        <PagarmeCheckoutModal
          show={showModal}
          onHide={() => setShowModal(false)}
          orderInternalId={inProgressOrder.internal_id}
          amountInCents={Math.round(price * 100)}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  )
}

export default Pagar
